import { api } from '@/shared/api/client'
import { url, type QueryValue } from '@/shared/lib/query'
import { dayEndIso, dayStartIso } from '@/shared/lib/format'
import type {
  CursorPage, NotificationDetailResponse, NotificationFacets,
  NotificationSummaryResponse, NotificationSummaryStats,
} from '@/shared/api/types'

export interface NotificationFilters {
  status: string[]
  channel: string[]
  type: string[]
  recipient: string
  recipientUserId: string
  q: string
  fromDay: string
  toDay: string
  entityType: string
  entityId: string
  requestId: string
  failedOnly: boolean
}

export const EMPTY_NOTIFICATION_FILTERS: NotificationFilters = {
  status: [], channel: [], type: [], recipient: '', recipientUserId: '', q: '',
  fromDay: '', toDay: '', entityType: '', entityId: '', requestId: '', failedOnly: false,
}

/** `to` is exclusive server-side — see the same note in the audit feature. */
export function notificationParams(f: NotificationFilters): Record<string, QueryValue> {
  return {
    status: f.status,
    channel: f.channel,
    type: f.type,
    recipient: f.recipient,
    recipientUserId: f.recipientUserId,
    q: f.q,
    from: f.fromDay ? dayStartIso(f.fromDay) : '',
    to: f.toDay ? dayEndIso(f.toDay) : '',
    entityType: f.entityType,
    entityId: f.entityId,
    requestId: f.requestId,
    // Only send it when true — `failedOnly=false` is noise in the URL.
    failedOnly: f.failedOnly ? true : '',
  }
}

export const listNotifications = (f: NotificationFilters, cursor?: string, limit = 50) =>
  api.get<CursorPage<NotificationSummaryResponse>>(
    url('/notifications', { ...notificationParams(f), cursor, limit }),
  )

export const notificationSummary = (f: NotificationFilters) =>
  api.get<NotificationSummaryStats>(url('/notifications/summary', notificationParams(f)))

export const notificationFacets = () => api.get<NotificationFacets>('/notifications/facets')

export const notificationDetail = (id: string) =>
  api.get<NotificationDetailResponse>(`/notifications/${id}`)

export const retryNotification = (id: string) =>
  api.post<NotificationDetailResponse>(`/notifications/${id}/retry`)

export const sendEmail = (body: { recipient: string; subject: string; bodyHtml: string }) =>
  api.post<{ id: string }>('/notifications/email', body)
